import React from 'react';

import { useUser } from '@adsk/alloy-provider';
import { Text } from '@adsk/alloy-react-box';
import { useLocale } from '@adsk/alloy-react-locale';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { MainEntityDisplay } from '.';
import { IN_PROGRESS_STEP } from '../../consts';
import {
  useUsersData,
  useEntityData,
  useCompaniesData,
  useRolesData,
  useEntitiesData,
} from '../../hooks';
import { SubmittalsItem } from '../../types';
import { sortTasksByRequiredAndAlphabeticallyAllTypes } from '../../utils/steps';

export enum SIZES {
  SMALL = 'small',
  MEDIUM = 'medium',
}

interface MultipleAssigneeCellProps {
  userIds: string[];
  itemData?: SubmittalsItem;
  stacked?: boolean;
  size?: SIZES;
}

const MultipleAssigneeDisplay: React.FC<MultipleAssigneeCellProps> = ({
  userIds,
  itemData,
  stacked,
  size,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const { user } = useUser();
  const currentUserId = user?.autodeskId || '';
  const currentUser = useEntityData(currentUserId);
  const currentUserCompanyId = currentUser.company || '';
  const currentUserRoleIds = currentUser.roles || [];

  const isItemInReview = itemData && itemData?.stateId === 'rev';
  const ballInCourtCompanies =
    (itemData && itemData?.ballInCourtCompanies) || [];
  const ballInCourtRoles = (itemData && itemData?.ballInCourtRoles) || [];
  const sortedReviewerIds: string[] = [];

  const finalBallInCourtArray: string[] = [];

  const ballInCourtUsersData = useUsersData(userIds);
  const ballInCourtCompaniesData = useCompaniesData(ballInCourtCompanies);
  const ballInCourtRolesData = useRolesData(ballInCourtRoles);

  if (isItemInReview) {
    // to display reviewers of current step first
    const reviewSteps = itemData ? itemData.steps : [];
    if (reviewSteps) {
      const inProgressStep = reviewSteps.find((step) => {
        if (step.status === IN_PROGRESS_STEP) return step;
        return undefined;
      });
      if (inProgressStep) {
        const sortedTasksAtInProgressStep =
          sortTasksByRequiredAndAlphabeticallyAllTypes(
            inProgressStep,
            ballInCourtUsersData,
            ballInCourtCompaniesData,
            ballInCourtRolesData,
          );
        sortedTasksAtInProgressStep.forEach((task) => {
          sortedReviewerIds.push(task.assignedTo);
        });
      }
    }
  }

  // to bring logged in user to the first if ball in court user array
  const ballInCourtArray = isItemInReview
    ? sortedReviewerIds
    : [...userIds, ...ballInCourtCompanies, ...ballInCourtRoles];

  const userRoles = ballInCourtArray.filter((value) =>
    currentUserRoleIds.includes(value),
  );
  const currentUserRoleId = userRoles[0] || '';

  const indexOfCurrentUser = ballInCourtArray.indexOf(currentUserId);
  const indexOfCurrentUserRole = ballInCourtArray.indexOf(currentUserRoleId);
  const indexOfCurrentUserCompany =
    ballInCourtArray.indexOf(currentUserCompanyId);
  if (indexOfCurrentUser > 0) {
    finalBallInCourtArray.push(ballInCourtArray[indexOfCurrentUser]);
    const otherBallInCourtUsers = ballInCourtArray.filter(
      (userId) => userId !== currentUserId,
    );
    finalBallInCourtArray.push(...otherBallInCourtUsers);
  } else if (indexOfCurrentUserRole > 0) {
    finalBallInCourtArray.push(ballInCourtArray[indexOfCurrentUserRole]);
    const otherBallInCourtUsers = ballInCourtArray.filter(
      (userId) => userId !== currentUserRoleId,
    );
    finalBallInCourtArray.push(...otherBallInCourtUsers);
  } else if (indexOfCurrentUserCompany > 0) {
    finalBallInCourtArray.push(ballInCourtArray[indexOfCurrentUserCompany]);
    const otherBallInCourtUsers = ballInCourtArray.filter(
      (userId) => userId !== currentUserCompanyId,
    );
    finalBallInCourtArray.push(...otherBallInCourtUsers);
  } else {
    finalBallInCourtArray.push(...ballInCourtArray);
  }
  const mainEntityId = finalBallInCourtArray[0];
  const ballInCourtEntities = useEntitiesData(finalBallInCourtArray);

  const mainEntity = useEntityData(mainEntityId);
  if (!mainEntity.name) {
    if (mainEntity.isLoading) {
      return <span>{T.translate('LOADING')}</span>;
    } else {
      return <Text color="charcoal500">{T.translate('FORMER_MEMBER')}</Text>;
    }
  }
  const { name, property } = mainEntity;

  const tooltipContent =
    finalBallInCourtArray.length > 1 ? (
      <>
        {ballInCourtEntities.length > 0 &&
          ballInCourtEntities.map((entity) => (
            <div key={entity.id}>
              {entity.property
                ? `${entity.name}(${entity.property})`
                : entity.name}
            </div>
          ))}
      </>
    ) : (
      <div>
        <div>{name}</div>
        <div>{property}</div>
      </div>
    );

  return (
    // to have tooltip and content even when the user name is not overflowing
    <Tooltip content={tooltipContent} trigger={Tooltip.TRIGGER_EVENTS.HOVER}>
      <OverflowTooltip autoTipOnEllipsis>
        <MainEntityDisplay
          name={name}
          property={property}
          currentUserId={currentUserId}
          currentUserCompanyId={currentUserCompanyId}
          currentUserRoleId={currentUserRoleId}
          totalEntities={finalBallInCourtArray}
          stacked={stacked}
          size={size}
        />
      </OverflowTooltip>
    </Tooltip>
  );
};

MultipleAssigneeDisplay.displayName = 'SubmittalsMultipleAssigneeDisplay';

MultipleAssigneeDisplay.defaultProps = {
  userIds: [],
  stacked: false,
  size: SIZES.MEDIUM,
};

export default MultipleAssigneeDisplay;
